import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import axios from "../../utils/axios.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import {
  conversationActions,
  fetchExchanges,
  selectConversationAction,
} from "../../store/conversation.tsx";
import type { Conversation, RootState } from "../../store/types.tsx";
import { setConversationUrl } from "./conversationUrl.ts";

interface ConversationSearchResult {
  conversation_id: string;
  conversation_title: string | null;
  exchange_id: string;
  prompt_snippet: string;
  response_snippet: string | null;
  created_at: string;
  rank: number;
}

interface ConversationSearchProps {
  closeDrawer: () => void;
  onConversationSelected?: () => void;
}

const ConversationSearch: React.FC<ConversationSearchProps> = ({
  closeDrawer,
  onConversationSelected,
}) => {
  const theme = useTheme();
  const isDesktopDialog = useMediaQuery(theme.breakpoints.up("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const conversations = useSelector(
    (state: RootState) => state.conversationState.conversations,
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [results, setResults] = React.useState<ConversationSearchResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);

  const recentConversations = React.useMemo(
    () => [
      ...conversations.pinned,
      ...conversations.last_7_days,
      ...conversations.last_30_days,
      ...conversations.other,
    ],
    [conversations],
  );

  React.useEffect(() => {
    const query = searchTerm.trim();
    if (query.length < 2) {
      setResults([]);
      setIsLoading(false);
      setHasSearched(false);
      return;
    }

    let ignore = false;
    setIsLoading(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await axios.get("/v1/conversation/search/", {
          params: { q: query },
          withCredentials: true,
        });
        if (!ignore) {
          setResults(response.data.objects as ConversationSearchResult[]);
          setHasSearched(true);
        }
      } catch (error) {
        if (!ignore) {
          setResults([]);
          setHasSearched(true);
          console.error("Error searching conversations:", error);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      ignore = true;
      window.clearTimeout(timeout);
    };
  }, [searchTerm]);

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm("");
    setResults([]);
    setHasSearched(false);
  };

  const selectConversation = (conversationId: string, exchangeId?: string) => {
    handleClose();
    closeDrawer();
    dispatch(conversationActions.setSelectedView("conversation"));
    dispatch(selectConversationAction(conversationId) as any);
    dispatch(fetchExchanges(conversationId) as any);
    onConversationSelected?.();
    setConversationUrl(navigate, conversationId, exchangeId);
  };

  const selectSearchResult = (result: ConversationSearchResult) => {
    selectConversation(result.conversation_id, result.exchange_id);
  };

  const selectRecentConversation = (conversation: Conversation) => {
    selectConversation(conversation.id);
  };

  return (
    <Box>
      <Typography
        variant="h4"
        onClick={() => setIsOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontSize: "1rem !important",
          width: "100%",
          lineHeight: "1",
          margin: "0",
          py: "6px",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            borderRadius: "25px",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "left",
          }}
        >
          {i18n._(
            msg({
              context:
                "Drawer action label for searching previous conversations",
              message: "Search Conversations",
            }),
          )}
        </Box>
      </Typography>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.spacing(2),
            marginLeft: isDesktopDialog
              ? `calc(${SETTINGS.drawerWidth}px)`
              : "auto",
            paddingBottom: theme.spacing(2.5),
            maxHeight: {
              xs: "min(560px, calc(100vh - 48px))",
              sm: "520px",
            },
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <DialogTitle
              sx={{
                color: theme.palette.primary.main,
                flexGrow: 1,
                p: 0,
              }}
            >
              {i18n._(
                msg({
                  context: "Dialog title for searching previous conversations",
                  message: "Search Conversations",
                }),
              )}
            </DialogTitle>
            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.text.disabled,
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: theme.palette.action.hover,
              borderRadius: theme.spacing(2),
              display: "flex",
              height: 48,
              px: 2,
              width: "100%",
            }}
          >
            <Box
              autoFocus
              component="input"
              value={searchTerm}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(event.target.value)
              }
              placeholder={i18n._(
                msg({
                  context: "Placeholder for searching previous conversations",
                  message: "Search conversations...",
                }),
              )}
              sx={{
                backgroundColor: "transparent",
                border: 0,
                color: theme.palette.text.primary,
                flexGrow: 1,
                fontSize: theme.typography.pxToRem(16),
                fontFamily: theme.typography.fontFamily,
                height: "100%",
                outline: 0,
                p: 0,
                "&::placeholder": {
                  color: theme.palette.text.disabled,
                  opacity: 1,
                },
              }}
            />
            {isLoading && <CircularProgress size={18} color="inherit" />}
          </Box>
        </Box>
        <DialogContent
          sx={{
            px: 2.5,
            pt: 1,
            pb: 0,
            overflowY: "auto",
          }}
        >
          {searchTerm.trim().length >= 2 ? (
            <Box>
              {results.length > 0 ? (
                <List sx={{ py: 0 }}>
                  {results.map((result) => (
                    <ListItemButton
                      key={`${result.conversation_id}-${result.exchange_id}`}
                      onClick={() => selectSearchResult(result)}
                      sx={{ borderRadius: "8px", px: 1.5, py: 1 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 34, color: theme.palette.primary.main }}
                      >
                        <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 21 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          result.conversation_title ||
                          i18n._(
                            msg({
                              context:
                                "Fallback title for an unnamed conversation search result",
                              message: "Untitled conversation",
                            }),
                          )
                        }
                        secondary={
                          result.response_snippet || result.prompt_snippet
                        }
                        primaryTypographyProps={{
                          noWrap: true,
                          fontSize: theme.typography.pxToRem(16),
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: theme.palette.text.disabled,
                            fontSize: theme.typography.pxToRem(14),
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                hasSearched &&
                !isLoading && (
                  <Typography
                    sx={{
                      mt: 4,
                      color: theme.palette.text.disabled,
                      fontSize: theme.typography.pxToRem(15),
                    }}
                  >
                    {i18n._(
                      msg({
                        context:
                          "Message shown when conversation search returns no results",
                        message: "No matching conversations",
                      }),
                    )}
                  </Typography>
                )
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              {recentConversations.length > 0 && (
                <>
                  <Typography
                    sx={{
                      color: theme.palette.text.disabled,
                      fontSize: theme.typography.pxToRem(15),
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {i18n._(
                      msg({
                        context:
                          "Header for recent conversations in the search dialog",
                        message: "Recent",
                      }),
                    )}
                  </Typography>
                  <List sx={{ py: 0 }}>
                    {recentConversations.slice(0, 8).map((conversation) => (
                      <ListItemButton
                        key={conversation.id}
                        onClick={() => selectRecentConversation(conversation)}
                        sx={{ borderRadius: "8px", px: 1.5, py: 1 }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 34,
                            color: theme.palette.primary.main,
                          }}
                        >
                          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 21 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            conversation.title ||
                            i18n._(
                              msg({
                                context:
                                  "Fallback title for an unnamed conversation search result",
                                message: "Untitled conversation",
                              }),
                            )
                          }
                          primaryTypographyProps={{
                            noWrap: true,
                            fontSize: theme.typography.pxToRem(16),
                            fontWeight: 500,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ConversationSearch;
